import type {
	default as HeaderTabs,
	GHeaderTab
} from '../aras/startup/headerTabs';
import type MainWindowCuiLayout from './MainWindowCuiLayout';
import type { ContextMenu } from 'core';

const SPLIT_CSS_CLASS = 'content-block_split';
const ACTIVE_TABBAR_CSS_CLASS = 'content-block__main-tabs_active';
const DOCK_FRAME_CSS_CLASS = 'content-block__tab_docked';

export default class HeaderTabsContainer {
	dockHeaderTabs: HeaderTabs;
	undockHeaderTabs: HeaderTabs;
	#requestId: null | number = null;
	#activeTabbar: null | HeaderTabs = null;
	options: {
		updateSelectedTabs?: MainWindowCuiLayout['actions']['updateSelectedTabs'];
	};
	constructor(
		dockHeaderTabs: HeaderTabs,
		undockHeaderTabs: HeaderTabs,
		options: {
			actions?: MainWindowCuiLayout['actions'];
		} = {}
	) {
		this.dockHeaderTabs = dockHeaderTabs;
		this.undockHeaderTabs = undockHeaderTabs;

		this.undockHeaderTabs.addEventListener('focusin', () => {
			this._setActiveTabbar(this.undockHeaderTabs);
		});
		this.dockHeaderTabs.addEventListener('focusin', () => {
			this._setActiveTabbar(this.dockHeaderTabs);
		});
		this.dockHeaderTabs.addEventListener('select', ((event: CustomEvent) => {
			if (!event.detail.id) {
				this.updateTabbarsStyle();
				this._setActiveTabbar(this.undockHeaderTabs);
			}
			this.#updateSelectedTabs();
		}) as EventListener);
		this.undockHeaderTabs.addEventListener('select', ((event: CustomEvent) => {
			if (!event.detail.id && this.dockHeaderTabs.data.size > 0) {
				this._setActiveTabbar(this.dockHeaderTabs);
			}
			this.#updateSelectedTabs();
		}) as EventListener);

		this.undockHeaderTabs.addEventListener('moved', ((event: CustomEvent) => {
			const { id } = event.detail;
			const tabData = this.undockHeaderTabs.data.get(id);
			tabData &&
				options.actions?.updateUndockedTabs(
					this.undockHeaderTabs.tabs,
					id,
					tabData
				);

			this.#updateSelectedTabs();
		}) as EventListener);
		this.dockHeaderTabs.addEventListener('moved', ((event: CustomEvent) => {
			const { id } = event.detail;
			const tabData = this.dockHeaderTabs.data.get(id);
			tabData &&
				options.actions?.updateDockedTabs(
					this.dockHeaderTabs.tabs,
					id,
					tabData
				);

			this.#updateSelectedTabs();
		}) as EventListener);

		this.undockHeaderTabs.options.updateTabState =
			options.actions?.updateUndockedTabs;
		this.dockHeaderTabs.options.updateTabState =
			options.actions?.updateDockedTabs;

		this.options = { updateSelectedTabs: options.actions?.updateSelectedTabs };
	}
	on(
		eventType: string,
		callback: (tabs: HeaderTabs, tabId: string, event: Event) => void
	) {
		const removeDockHandler = this.dockHeaderTabs.on(
			eventType,
			(tabId, event) => callback(this.dockHeaderTabs, tabId, event)
		);
		const removeUndockHandler = this.undockHeaderTabs.on(
			eventType,
			(tabId, event) => callback(this.undockHeaderTabs, tabId, event)
		);
		return () => {
			removeDockHandler();
			removeUndockHandler();
		};
	}
	attachContextMenu(contextMenu: ContextMenu) {
		if (!contextMenu) {
			return;
		}

		const tabsHandlers = {
			dockTab: (id: string) => this.dockTab(id),
			undockTab: (id: string) => this.undockTab(id),
			isDockedTab: (id: string) => this.isDockedTab(id)
		};
		this.on('contextmenu', (tabbar, tabId, event) => {
			if (!(event instanceof MouseEvent)) {
				return;
			}

			event.preventDefault();
			const currentTarget = tabbar.data.get(tabId);

			contextMenu.show(
				{ x: event.clientX, y: event.clientY },
				{
					tabs: [...tabbar.tabs],
					currentTarget: { ...currentTarget, id: tabId },
					closeTab: (id: string) => tabbar.removeTab(id),
					closeTabs: (ids: Array<string>) => tabbar.closeTabs(ids),
					closeOtherTabs: (id: string) => tabbar.closeOtherTabs(id),
					openInTearOff: (id: string) => tabbar.clickOpenInTearOff(id),
					...tabsHandlers
				}
			);
		});
	}
	isDockedTab(tabId: string) {
		return this.dockHeaderTabs.tabs.includes(tabId);
	}
	_moveTab(
		tabId: string,
		tabbarFrom: HeaderTabs,
		tabbarTo: HeaderTabs,
		position?: number
	) {
		const data = tabbarFrom.ejectTab(tabId);
		if (data) {
			data.parentTab = null;
			tabbarTo.addTab(tabId, data, position);
		}

		const frame = document.getElementById(tabId);
		frame?.classList.toggle(
			DOCK_FRAME_CSS_CLASS,
			tabbarTo === this.dockHeaderTabs
		);

		if (data) {
			this.undockHeaderTabs.options.updateTabState?.(
				this.undockHeaderTabs.tabs,
				tabId,
				data
			);
			this.dockHeaderTabs.options.updateTabState?.(
				this.dockHeaderTabs.tabs,
				tabId,
				data
			);
		}
	}
	dockTab(tabId: string) {
		this._moveTab(tabId, this.undockHeaderTabs, this.dockHeaderTabs);
		this.updateTabbar(tabId, this.dockHeaderTabs);
	}
	undockTab(tabId: string) {
		if (this.dockHeaderTabs.tabs.length === 1) {
			this.exitSplitscreen();
			return;
		}
		this._moveTab(tabId, this.dockHeaderTabs, this.undockHeaderTabs);
		this.updateTabbar(tabId, this.undockHeaderTabs);
	}
	#updateSelectedTabs() {
		const selectedTabs = [
			this.undockHeaderTabs.selectedTab,
			this.dockHeaderTabs.selectedTab
		].filter((v): v is string => !!v);

		this.options.updateSelectedTabs?.(selectedTabs);
	}
	updateTabbarsStyle() {
		const isNeedSplit = this.dockHeaderTabs.data.size > 0;
		const tabsParentElement = this.dockHeaderTabs.parentElement;
		tabsParentElement?.classList.toggle(SPLIT_CSS_CLASS, isNeedSplit);

		const splitter = tabsParentElement?.querySelector(
			'aras-splitter'
		) as HTMLElement;
		const splitterWidth = splitter?.offsetWidth;
		if (splitterWidth === 0 && isNeedSplit) {
			throw new Error(
				'The width of the splitter cannot be zero if split screen mode is enabled'
			);
		}

		const splitterColumnWidth = `${splitterWidth}px`;
		const startColumnSize = `calc(50% - ${splitterWidth / 2}px)`;
		const splitColumnStyle = `${startColumnSize} ${splitterColumnWidth} ${startColumnSize}`;

		const shouldUpdateStyles = !(
			isNeedSplit && tabsParentElement?.style.gridTemplateColumns
		);

		if (shouldUpdateStyles && tabsParentElement) {
			tabsParentElement.style.gridTemplateColumns = isNeedSplit
				? splitColumnStyle
				: '';
		}
	}
	updateActiveTabbarObserver() {
		const isNeedSplit = this.dockHeaderTabs.data.size > 0;
		if (isNeedSplit) {
			this.startActiveTabbarObserver();
		} else {
			this.stopActiveTabbarObserver();
		}
	}
	updateTabbar(tabId: string, tabbar: HeaderTabs) {
		tabbar.selectTab(tabId);
		this._setActiveTabbar(tabbar);
		this.updateTabbarsStyle();
		this.updateActiveTabbarObserver();
		this.#updateSelectedTabs();
	}
	startActiveTabbarObserver() {
		if (this.#requestId) {
			return;
		}

		this.#requestId = requestAnimationFrame(this._checkActiveTabbar.bind(this));
	}
	stopActiveTabbarObserver() {
		if (this.#requestId) {
			cancelAnimationFrame(this.#requestId);
		}
		this.#requestId = null;
	}
	_checkActiveTabbar() {
		setTimeout(() => {
			if (!this.#requestId) {
				return;
			}

			this.#requestId = requestAnimationFrame(
				this._checkActiveTabbar.bind(this)
			);
		}, 200);

		const activeElement = document.activeElement;
		const activeElementTagName = activeElement && activeElement.tagName;

		if (activeElementTagName === 'IFRAME') {
			const tabbar = this.getTabbarByTabId(activeElement?.id || '');
			if (tabbar) {
				this._setActiveTabbar(tabbar);
			}
		}
	}
	_setActiveTabbar(tabbar: HeaderTabs) {
		if (this.#activeTabbar === tabbar) {
			return;
		}
		this.#activeTabbar = tabbar;
		const isDockTabbarActive = tabbar === this.dockHeaderTabs;
		this.dockHeaderTabs.classList.toggle(
			ACTIVE_TABBAR_CSS_CLASS,
			isDockTabbarActive
		);
		this.undockHeaderTabs.classList.toggle(
			ACTIVE_TABBAR_CSS_CLASS,
			!isDockTabbarActive
		);
	}
	getTabbarByTabId(tabId: string) {
		if (this.dockHeaderTabs.tabs.includes(tabId)) {
			return this.dockHeaderTabs;
		}

		if (this.undockHeaderTabs.tabs.includes(tabId)) {
			return this.undockHeaderTabs;
		}

		return null;
	}
	isExistAnyTabs() {
		const dockHeaderTabsExist = !!this.dockHeaderTabs.tabs.length;
		const undockHeaderTabsExist = !!this.undockHeaderTabs.tabs.length;

		return dockHeaderTabsExist || undockHeaderTabsExist;
	}
	getSearchGridTabs(id: string) {
		return [
			...this.dockHeaderTabs.getSearchGridTabs(id),
			...this.undockHeaderTabs.getSearchGridTabs(id)
		];
	}
	openPage(url: string, icon: string, label: string) {
		if (!url) {
			return null;
		}

		const openedPageTabId = this.dockHeaderTabs.tabs.find(
			(tab) => tab.indexOf('page_' + url) !== -1
		);

		if (openedPageTabId) {
			this.dockHeaderTabs.selectTab(openedPageTabId);
			return;
		}

		return this.undockHeaderTabs.openPage(url, icon, label);
	}
	openCuiLayout(
		layoutName: string,
		location: string,
		name: string,
		tabUiData: GHeaderTab<'CuiLayout'>
	) {
		if (!layoutName) {
			return null;
		}

		const openedCuiLayoutId = this.dockHeaderTabs.tabs.find(
			(tab) => tab === `layout_${name}`
		);
		if (openedCuiLayoutId) {
			this.dockHeaderTabs.selectTab(openedCuiLayoutId);
			return;
		}

		return this.undockHeaderTabs.openCuiLayout(
			layoutName,
			location,
			name,
			tabUiData
		);
	}
	openForm(formId: undefined | string, icon: string, label: string) {
		if (!formId) {
			return null;
		}
		const formName = aras.MetadataCache.GetFormName(formId);
		const openedFormTabId = this.dockHeaderTabs.tabs.find(
			(tab) => tab.indexOf('form_' + formId + formName + '_') !== -1
		);

		if (openedFormTabId) {
			this.dockHeaderTabs.selectTab(openedFormTabId);
			return;
		}

		return this.undockHeaderTabs.openForm(formId, icon, label);
	}
	toggleSplitScreenMode() {
		const isSplitScreenModeEnabled = this.dockHeaderTabs.tabs.length > 0;
		if (isSplitScreenModeEnabled) {
			this.exitSplitscreen();
		} else {
			const selectedTab = this.undockHeaderTabs.selectedTab;
			selectedTab && this.dockTab(selectedTab);
		}
	}
	exitSplitscreen() {
		const selectedTab = this.#activeTabbar?.selectedTab;
		const dockHeaderTabs = [...this.dockHeaderTabs.tabs];
		dockHeaderTabs.forEach((tabId, index) => {
			this._moveTab(tabId, this.dockHeaderTabs, this.undockHeaderTabs, index);
		});
		selectedTab && this.updateTabbar(selectedTab, this.undockHeaderTabs);
	}
}
